/**
 * @voltxampere/presets — NPN common-emitter amplifier (#9)
 *
 * Mirrors the Sprint 7 §7 smoke-test biasing: V_CC = 12 V, Rb to
 * base, Rc to collector, Re from emitter to gnd. Active-region
 * NPN with the Ebers-Moll defaults (Is=1e-16, Bf=100). Expected
 * V_BE in the 0.6-0.75 V band — checked at the base net relative
 * to the emitter via the engine's solved node voltages.
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. Vcc at top-left,
 * Rb (rot 90) and Rc (rot 90) hang from the Vcc rail to the base
 * and collector pins of Q1 at the canvas center, Re (rot 90)
 * carries the emitter down to the GND rail. Wire endpoints derive
 * directly from the BJT pin-local table (B=(10,13), C=(32,4),
 * E=(32,22)) shifted by the viewBox center (20,13).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const npnCommonEmitter = {
    id: "npn-common-emitter",
    name: { tr: "NPN Ortak Emiterli Yükselteç", en: "NPN Common-Emitter Amplifier" },
    category: "amplifier",
    difficulty: 3,
    description: {
        tr: "Klasik NPN ortak emiterli yükselteç. Vcc=12V, Rb=470kΩ, Rc=4.7kΩ, Re=1kΩ. Aktif bölge biaslama.",
        en: "Classic NPN common-emitter amplifier. Vcc=12V, Rb=470kΩ, Rc=4.7kΩ, Re=1kΩ. Active-region bias.",
    },
    circuit: makeCircuit({
        slot: 9,
        name: "NPN Common-Emitter",
        description: "Sprint 7 §7 reference biasing",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "b" }),
            makeNet({ name: "c" }),
            makeNet({ name: "e" }),
        ],
        components: [
            makeComponent({
                id: "Vcc",
                type: "voltage-source",
                params: { voltage: 12 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "Vcc",
                position: { x: -200, y: -180 },
                rotation: 0,
            }),
            makeComponent({
                id: "Rb",
                type: "resistor",
                params: { resistance: 470_000 },
                terminals: { p1: "net_vcc", p2: "net_b" },
                label: "Rb",
                position: { x: -60, y: -90 },
                rotation: 90,
            }),
            makeComponent({
                id: "Rc",
                type: "resistor",
                params: { resistance: 4_700 },
                terminals: { p1: "net_vcc", p2: "net_c" },
                label: "Rc",
                position: { x: 60, y: -90 },
                rotation: 90,
            }),
            makeComponent({
                id: "Re",
                type: "resistor",
                params: { resistance: 1_000 },
                terminals: { p1: "net_e", p2: "net_gnd" },
                label: "Re",
                position: { x: 60, y: 100 },
                rotation: 90,
            }),
            makeComponent({
                id: "Q1",
                type: "bjt-npn",
                params: {},
                terminals: { base: "net_b", collector: "net_c", emitter: "net_e" },
                label: "Q1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -200, y: 200 },
                rotation: 0,
            }),
        ],
        wires: [
            // vcc rail leg A: V1.pos (-200, -190) → Rb.p1 (-60, -110)
            {
                id: "w_vcc_rb",
                netId: "net_vcc",
                path: [
                    { x: -200, y: -190 },
                    { x: -200, y: -210 },
                    { x: -60, y: -210 },
                    { x: -60, y: -110 },
                ],
                style: "orthogonal",
            },
            // vcc rail leg B: tee from (-60, -210) → Rc.p1 (60, -110)
            {
                id: "w_vcc_rc",
                netId: "net_vcc",
                path: [
                    { x: -60, y: -210 },
                    { x: 60, y: -210 },
                    { x: 60, y: -110 },
                ],
                style: "orthogonal",
            },
            // base net: Rb.p2 (-60, -70) → Q1.B (-10, 0)
            {
                id: "w_b",
                netId: "net_b",
                path: [
                    { x: -60, y: -70 },
                    { x: -60, y: 0 },
                    { x: -10, y: 0 },
                ],
                style: "orthogonal",
            },
            // collector net: Rc.p2 (60, -70) → Q1.C (12, -9)
            {
                id: "w_c",
                netId: "net_c",
                path: [
                    { x: 60, y: -70 },
                    { x: 60, y: -9 },
                    { x: 12, y: -9 },
                ],
                style: "orthogonal",
            },
            // emitter net: Q1.E (12, 9) → Re.p1 (60, 80)
            {
                id: "w_e",
                netId: "net_e",
                path: [
                    { x: 12, y: 9 },
                    { x: 60, y: 9 },
                    { x: 60, y: 80 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg A: V1.neg (-200, -170) → GND.p (-200, 191)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -200, y: -170 },
                    { x: -200, y: 191 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg B: Re.p2 (60, 120) → GND.p (-200, 191)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 60, y: 120 },
                    { x: 60, y: 240 },
                    { x: -200, y: 240 },
                    { x: -200, y: 191 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        // V(b) is the absolute base-node voltage relative to ground.
        // V_BE ≈ 0.79 V sits ATOP V(emitter) ≈ 1.98 V (set by emitter
        // degeneration through Re), so V(b) ≈ 2.77 V. The ±0.4 V band
        // covers Sprint 7 §7's analytical biasing math without
        // pinning the exact frozen-regression sentinel — that lives
        // in the engine's own regression suite.
        {
            node: "b",
            analysis: "dc",
            value: 2.77,
            tolerance: 0.4,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Aktif bölge BJT biasi: V_BE ≈ 0.7V, V_CE > 0.2V → çıkış doğrusal yükseltme.",
            en: "Active-region BJT bias: V_BE ≈ 0.7V, V_CE > 0.2V → linear output amplification.",
        },
        {
            tr: "Emiter direnci Re biasi termal kaymalara karşı stabilize eder.",
            en: "Emitter resistor Re stabilises bias against thermal drift.",
        },
        {
            tr: "Akım kazancı β = Ic/Ib (varsayılan 100).",
            en: "Current gain β = Ic/Ib (default 100).",
        },
    ],
    references: ["Gray & Meyer, Analysis and Design of Analog ICs, Ch. 1"],
};
//# sourceMappingURL=npn-common-emitter.js.map